import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDownCircle, ArrowUpCircle, Loader2, Pencil, PlusCircle, Trash2, Wallet } from 'lucide-react';
import { AmministrazioneService } from '../services/apiService';
import { MovimentoManuale } from '../types';
import { useToast } from './ToastContext';
import { Modal } from './Modal';
import NumberInput from './NumberInput';

// Entrate e uscite una tantum dell'agenzia: a differenza delle spese fisse contano solo
// alla loro data. Le entrate vanno nell'incassato (e nel fatturato se fatturate), le uscite
// nei costi. Si inseriscono su una sede precisa, come le spese fisse.

type Props = {
    sedeId: number | string | null | undefined;
    onChanged?: () => void;
    // Mese "YYYY-MM" gestito dalla pagina; senza, la card usa il mese corrente.
    mese?: string;
    onMeseChange?: (mese: string) => void;
};

type Bozza = {
    id?: number;
    sedeId?: number;
    tipo: 'entrata' | 'uscita';
    data: string;
    voce: string;
    importo: number;
    fatturato: boolean;
};

const pad = (n: number) => String(n).padStart(2, '0');
const oggi = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
export const limitiMese = (mese: string) => {
    const [y, m] = mese.split('-').map(Number);
    const ultimo = new Date(y, m, 0).getDate();
    return { from: `${mese}-01`, to: `${mese}-${pad(ultimo)}` };
};
const formatCurrency = (n: number) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(n);
const formatDate = (d: string) => {
    const m = (d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : '-';
};

const MovimentiUnaTantumCard: React.FC<Props> = ({ sedeId, onChanged, mese: meseEsterno, onMeseChange }) => {
    const { addToast } = useToast();
    const [meseInterno, setMeseInterno] = useState(() => oggi().slice(0, 7));
    const mese = meseEsterno ?? meseInterno;
    const setMese = (m: string) => { if (onMeseChange) onMeseChange(m); else setMeseInterno(m); };
    const [righe, setRighe] = useState<MovimentoManuale[]>([]);
    const [loading, setLoading] = useState(true);
    const [bozza, setBozza] = useState<Bozza | null>(null);
    const [saving, setSaving] = useState(false);
    const [selezionaSede, setSelezionaSede] = useState(false);

    const sedeNumerica = typeof sedeId === 'number' ? sedeId
        : (typeof sedeId === 'string' && sedeId !== 'ALL' && sedeId !== '' ? Number(sedeId) : null);

    const carica = useCallback(async () => {
        setLoading(true);
        try {
            const { from, to } = limitiMese(mese);
            const res = await AmministrazioneService.getMovimentiManuali({ sedeId: sedeNumerica, from, to });
            setRighe(Array.isArray(res?.data) ? res.data : []);
        } catch {
            setRighe([]);
            addToast('Impossibile caricare le entrate e uscite una tantum', 'error');
        } finally {
            setLoading(false);
        }
    }, [mese, sedeNumerica, addToast]);

    useEffect(() => { carica(); }, [carica]);

    const totali = useMemo(() => righe.reduce(
        (acc, r) => r.tipo === 'entrata'
            ? { ...acc, entrate: acc.entrate + r.importo }
            : { ...acc, uscite: acc.uscite + r.importo },
        { entrate: 0, uscite: 0 }
    ), [righe]);

    const nuova = (tipo: 'entrata' | 'uscita') => {
        if (sedeNumerica === null) { setSelezionaSede(true); return; }
        const { from, to } = limitiMese(mese);
        const o = oggi();
        setBozza({ tipo, data: o >= from && o <= to ? o : from, voce: '', importo: 0, fatturato: false, sedeId: sedeNumerica });
    };

    const modifica = (r: MovimentoManuale) => setBozza({
        id: r.id, sedeId: r.sedeId, tipo: r.tipo, data: r.data, voce: r.voce,
        importo: r.importo, fatturato: !!r.fatturato,
    });

    const salva = async () => {
        if (!bozza || !bozza.sedeId) return;
        if (!bozza.voce.trim() || !(bozza.importo > 0) || !bozza.data) {
            addToast('Inserisci data, descrizione e un importo valido', 'error');
            return;
        }
        setSaving(true);
        try {
            const payload = {
                sedeId: bozza.sedeId, tipo: bozza.tipo, data: bozza.data, voce: bozza.voce.trim(),
                importo: bozza.importo, fatturato: bozza.tipo === 'entrata' && bozza.fatturato,
            };
            if (bozza.id) await AmministrazioneService.updateMovimentoManuale(bozza.id, payload);
            else await AmministrazioneService.addMovimentoManuale(payload);
            addToast(bozza.tipo === 'entrata' ? 'Entrata salvata' : 'Uscita salvata', 'success');
            setBozza(null);
            carica();
            onChanged?.();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Errore durante il salvataggio', 'error');
        } finally {
            setSaving(false);
        }
    };

    const elimina = async (r: MovimentoManuale) => {
        if (!window.confirm(`Eliminare "${r.voce}" (${formatCurrency(r.importo)})?`)) return;
        try {
            await AmministrazioneService.deleteMovimentoManuale(r.id);
            addToast('Movimento eliminato', 'success');
            carica();
            onChanged?.();
        } catch (e: any) {
            addToast(e?.response?.data?.message || "Errore durante l'eliminazione", 'error');
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b flex flex-wrap gap-3 justify-between items-center bg-gray-50">
                <div className="flex items-center gap-2">
                    <Wallet className="text-gray-400" size={20}/>
                    <h3 className="font-bold text-gray-800 uppercase text-xs tracking-wider">Entrate e uscite una tantum</h3>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <input
                        type="month"
                        aria-label="Mese"
                        value={mese}
                        onChange={e => e.target.value && setMese(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white"
                    />
                    <span className="text-xs text-gray-500 font-medium">
                        <span className="text-emerald-700 font-bold">+{formatCurrency(totali.entrate)}</span>
                        {' / '}
                        <span className="text-red-600 font-bold">−{formatCurrency(totali.uscite)}</span>
                    </span>
                    <button
                        onClick={() => nuova('entrata')}
                        className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition-colors"
                    >
                        <PlusCircle size={14}/> Entrata
                    </button>
                    <button
                        onClick={() => nuova('uscita')}
                        className="bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition-colors"
                    >
                        <PlusCircle size={14}/> Uscita
                    </button>
                </div>
            </div>
            <div className="p-4">
                {loading ? (
                    <div className="py-4 flex justify-center"><Loader2 className="animate-spin text-gray-400" size={20}/></div>
                ) : righe.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">
                        Nessuna entrata o uscita una tantum in questo mese. Valgono solo nel mese della loro data, a differenza delle spese fisse.
                    </p>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {righe.map(r => (
                            <div key={r.id} className="flex items-center gap-3 py-2.5">
                                {r.tipo === 'entrata'
                                    ? <ArrowDownCircle size={18} className="text-emerald-600 flex-shrink-0"/>
                                    : <ArrowUpCircle size={18} className="text-red-500 flex-shrink-0"/>}
                                <span className="text-xs text-gray-500 w-20 flex-shrink-0">{formatDate(r.data)}</span>
                                <span className="text-sm text-gray-800 flex-1 min-w-0 truncate" title={r.voce}>
                                    {r.voce}
                                    {r.tipo === 'entrata' && r.fatturato && (
                                        <span className="ml-2 text-[10px] font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">Fatturata</span>
                                    )}
                                </span>
                                <span className={`text-sm font-bold whitespace-nowrap ${r.tipo === 'entrata' ? 'text-emerald-700' : 'text-red-600'}`}>
                                    {r.tipo === 'entrata' ? '+' : '−'}{formatCurrency(r.importo)}
                                </span>
                                <button onClick={() => modifica(r)} className="text-gray-400 hover:text-blue-600 p-1" title="Modifica">
                                    <Pencil size={15}/>
                                </button>
                                <button onClick={() => elimina(r)} className="text-gray-400 hover:text-red-600 p-1" title="Elimina">
                                    <Trash2 size={15}/>
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {bozza && (
                <Modal
                    title={`${bozza.id ? 'Modifica' : 'Nuova'} ${bozza.tipo === 'entrata' ? 'entrata' : 'uscita'} una tantum`}
                    onClose={() => setBozza(null)}
                >
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-2">
                            {(['uscita', 'entrata'] as const).map(t => (
                                <button
                                    key={t}
                                    type="button"
                                    onClick={() => setBozza({ ...bozza, tipo: t })}
                                    className={`py-2 rounded-lg text-sm font-bold border transition-colors ${bozza.tipo === t
                                        ? (t === 'entrata' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-red-600 text-white border-red-600')
                                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                                >
                                    {t === 'entrata' ? 'Entrata' : 'Uscita'}
                                </button>
                            ))}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Data</label>
                            <input
                                type="date"
                                className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                                value={bozza.data}
                                onChange={e => setBozza({ ...bozza, data: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Descrizione</label>
                            <input
                                type="text"
                                maxLength={255}
                                placeholder={bozza.tipo === 'entrata' ? 'es. Rimborso assicurazione' : 'es. Riparazione auto aziendale'}
                                className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                                value={bozza.voce}
                                onChange={e => setBozza({ ...bozza, voce: e.target.value })}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Importo (€)</label>
                            <NumberInput
                                min={0}
                                step="0.01"
                                placeholder="0.00"
                                className="w-full p-2.5 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                                value={bozza.importo || ''}
                                onChange={e => setBozza({ ...bozza, importo: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                            />
                        </div>
                        {bozza.tipo === 'entrata' && (
                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={bozza.fatturato}
                                    onChange={e => setBozza({ ...bozza, fatturato: e.target.checked })}
                                />
                                Fatturata (conta anche nel fatturato)
                            </label>
                        )}
                        <button
                            onClick={salva}
                            disabled={saving}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-60 flex items-center justify-center gap-2"
                        >
                            {saving && <Loader2 size={16} className="animate-spin"/>}
                            Salva
                        </button>
                    </div>
                </Modal>
            )}

            {selezionaSede && (
                <Modal title="Seleziona prima una sede" onClose={() => setSelezionaSede(false)}>
                    <div className="space-y-4">
                        <p className="text-sm text-slate-700">
                            Le entrate e le uscite una tantum sono legate a una <strong>sede specifica</strong>.
                            Seleziona prima una sede dalla sidebar (in alto a sinistra), poi torna qui.
                        </p>
                        <button
                            onClick={() => setSelezionaSede(false)}
                            className="w-full bg-blue-600 text-white py-2.5 rounded-lg font-bold hover:bg-blue-700"
                        >
                            Ho capito
                        </button>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default MovimentiUnaTantumCard;
